import React, { useContext, useMemo } from "react";

import { useSearchParams } from "../navigation";
import { decode, encode } from "./filter_url";
import { FilterConfig } from "./utils";
import { FamilyMemberStatus } from "src/patents/patents";

const FilterContext = React.createContext({
  showActiveOnly: true,
  setShowActiveOnly: (arg: boolean | ((s: boolean) => any)) => {},

  // the type here could also be a generic 'IpStatus'
  ipStates: [] as FamilyMemberStatus[],
  setIpStates: (states: FamilyMemberStatus[]) => {},

  activeFilters: [] as FilterConfig[],
  setActiveFilters: (
    arg: FilterConfig[] | ((a: FilterConfig[]) => FilterConfig[])
  ) => {}, // Dispatch<SetState<FilterConfig[]>>
  addFilter: (arg: FilterConfig) => {},

  updateActiveFilter: (arg: FilterConfig, index: number) => {},
  removeActiveFilter: (index: number) => {},
});

export const possibleIpStates: FamilyMemberStatus[] = [
  'in-preparation',
  'pending',
  'granted',
  'stopped',
]

export const activeOnlyStates = new Set(possibleIpStates.slice(0, 3))

function calculateActiveOnly(ipStates: FamilyMemberStatus[]) {
  if (ipStates.length === 3 && ipStates.every(s => activeOnlyStates.has(s))) {
    return true
  } else if (ipStates.length === 4 || ipStates.length === 0) {
    // 0 is like 'not set', i.e. get me everything
    return false
  }
  return undefined
}

export function FilterProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams()

  //const [ipStates, setIpStates] = useState<FamilyMemberStatus[]>(['in-preparation', 'pending', 'granted'])

  const ipStates = useMemo(() => {
    //const hasNoStates = searchParams.get('states') === null
    //if (hasNoStates) return [...activeOnlyStates]
    //return searchParams.getAll('states')
    const states = searchParams.get('states')
    if (states === null) return [...activeOnlyStates]
    return states.split(',') as FamilyMemberStatus[]
  }, [searchParams])

  // TODO problem if states is empty == not set in search params
  const setIpStates = (states: FamilyMemberStatus[]) => {
    setSearchParams(prev => {
      if (prev.has('states')) {
        prev.delete('states')
      } 
      const activeOnly = calculateActiveOnly(states)
      if (activeOnly !== true) {
        prev.set('states', states.join(','))
        // states.forEach(s => prev.append('states', s))
      }
      return prev
    })
  }
  const showActiveOnly = calculateActiveOnly(ipStates)

  function setShowActiveOnly(activeOnly: boolean) {
    if (activeOnly) {
      setIpStates([...activeOnlyStates])
    } else {
      setIpStates(possibleIpStates)
    }
  }

  //const showActiveOnly = useMemo(() => decodeActiveOnly(searchParams.get('activeOnly')), [searchParams])
  //const setShowActiveOnly = (activeOnly: boolean) => {
  //  setSearchParams(prev => {
  //    if (prev.has('activeOnly')) {
  //      prev.delete('activeOnly')
  //    }
  //    if (!activeOnly) {
  //      prev.append('activeOnly', 'false')
  //    }
  //    return prev
  //  })
  //}

  const activeFilters = useMemo(() => decode(searchParams.get('filter')), [searchParams])
  const setActiveFilters = (update: (filters: FilterConfig[]) => FilterConfig[]) => {
    const filters = update(activeFilters)
    setSearchParams(prev => {
      if (prev.has('filter')) {
        prev.delete('filter')
      }

      if (filters.length === 0)
        return prev

      //console.log({ filters })
      prev.append('filter', encode(filters))
      // console.log(prev.toString()) // TODO
      return prev
    })
      // ({...prev, filter: encode(filters)}))
  }

  function updateActiveFilter(filter: FilterConfig, index: number) {
    setActiveFilters((fs: FilterConfig[]) =>
      fs.map((f, i) => (i === index ? filter : f))
    );
  }

  function removeActiveFilter(index: number) {
    setActiveFilters((fs: FilterConfig[]) => fs.filter((f, i) => i !== index));
  }

  const value = {
    showActiveOnly,
    setShowActiveOnly,
    ipStates,
    setIpStates,
    activeFilters,
    setActiveFilters,
    addFilter: (filter: FilterConfig) => setActiveFilters(fs => [...fs, filter]),
    updateActiveFilter,
    removeActiveFilter,
  };
  
  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}

export function useFilter() {
  return useContext(FilterContext);
}
